/**
 * Notification Panel
 *
 * The main dropdown/sheet that displays the notification list.
 * Features: category filtering, cursor-based pagination, mark all read,
 * loading skeletons, and empty state.
 *
 * @module components/notifications/NotificationPanel
 */

import React, { useState, useCallback, useMemo } from 'react';
import { AnimatePresence } from 'motion/react';
import { CheckCheck, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLocaleNavigate } from '../../lib/i18n/exports';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDismissNotification,
} from '../../entities/notification/hooks/useNotificationQueries';
import { notificationQueryKeys } from '@lib/query/client';
import NotificationItem from './NotificationItem';
import NotificationCategoryFilter from './NotificationCategoryFilter';
import NotificationEmptyState from './NotificationEmptyState';
import { NotificationListSkeleton } from './NotificationItemSkeleton';
import {
  resolveNotificationAction,
  type OnboardingModal,
} from '../../entities/notification/utils/resolveNotificationRoute';
import { PhoneManagementModal } from '../../features/user-dashboard/components/settings/PhoneManagementModal';
import { TwoFactorSetupModal } from '../../features/user-dashboard/components/settings/TwoFactorSetupModal';
import { VerificationQuickAction } from '../../features/user-dashboard/components/verification/VerificationQuickAction';
import { useToastActions } from '../../shared/ui/toast';
import type {
  Notification,
  NotificationCategory,
} from '../../entities/notification/types';

interface NotificationPanelProps {
  /** Close the panel after navigation */
  onClose: () => void;
}

/** Category keys for empty state messaging (mapped to i18n) */
const CATEGORY_I18N_KEYS: Record<NotificationCategory, string> = {
  CAMPAIGNS: 'categoryLabels.campaigns',
  TRANSACTIONS: 'categoryLabels.transactions',
  SECURITY: 'categoryLabels.security',
  SYSTEM: 'categoryLabels.system',
  ONBOARDING: 'categoryLabels.onboarding',
  MARKETING: 'categoryLabels.marketing',
};

/**
 * Notification panel content (used inside PortalDropdown or bottom sheet)
 */
const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose }) => {
  const { t } = useTranslation('notifications');
  const navigate = useLocaleNavigate();
  const queryClient = useQueryClient();
  const toast = useToastActions();
  const [activeCategory, setActiveCategory] = useState<
    NotificationCategory | 'ALL'
  >('ALL');
  const [activeModal, setActiveModal] = useState<OnboardingModal | null>(null);

  // Build filters based on active category
  const filters = useMemo(
    () =>
      activeCategory === 'ALL' ? {} : { category: activeCategory },
    [activeCategory]
  );

  // Data fetching
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useNotifications(filters);

  // Mutations
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const dismissNotification = useDismissNotification();

  // Flatten paginated data (filter nullish for safety)
  const notifications = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.notifications ?? [])
        .filter(Boolean) ?? [],
    [data]
  );

  const totalUnread = data?.pages[0]?.totalUnread ?? 0;

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleNotificationPress = useCallback(
    (notification: Notification) => {
      // Mark as read if unread
      if (!notification.read) {
        markAsRead.mutate(notification.id);
      }

      // Resolve action: modal (in-context) or navigate (route change)
      const action = resolveNotificationAction(notification, 'user');

      if (action?.type === 'modal') {
        setActiveModal(action.modal);
        return;
      }

      if (action?.type === 'navigate') {
        navigate(action.route);
        onClose();
      }
    },
    [markAsRead, navigate, onClose]
  );

  /** Called when an in-context modal completes its action successfully */
  const handleModalSuccess = useCallback(
    (toastMessage: string) => {
      setActiveModal(null);
      toast.success(toastMessage);
      // Refresh notifications to update unread count and status
      void queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.notifications.all,
      });
      onClose();
    },
    [toast, queryClient, onClose]
  );

  const handleModalClose = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleDismiss = useCallback(
    (id: string) => {
      dismissNotification.mutate(id);
    },
    [dismissNotification]
  );

  const handleMarkAllRead = useCallback(() => {
    if (totalUnread > 0) {
      markAllAsRead.mutate();
    }
  }, [markAllAsRead, totalUnread]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col w-full max-h-[calc(100vh-120px)] sm:w-[400px]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('panel.title')}
        </h2>
        {totalUnread > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markAllAsRead.isPending}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            {t('panel.markAllRead')}
          </button>
        )}
      </div>

      {/* Category filter */}
      <NotificationCategoryFilter
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      {/* Notification list */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {isLoading ? (
          <NotificationListSkeleton count={5} />
        ) : notifications.length === 0 ? (
          <NotificationEmptyState
            categoryLabel={
              activeCategory !== 'ALL'
                ? t(CATEGORY_I18N_KEYS[activeCategory])
                : undefined
            }
          />
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            <AnimatePresence mode="popLayout" initial={false}>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onPress={handleNotificationPress}
                  onDismiss={handleDismiss}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Load more */}
        {hasNextPage && (
          <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
            <button
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('panel.loading')}
                </>
              ) : (
                t('panel.loadMore')
              )}
            </button>
          </div>
        )}
      </div>

      {/* ── In-context action modals ──────────────────────────────── */}

      <PhoneManagementModal
        isOpen={activeModal === 'phone'}
        onClose={handleModalClose}
        initialState="add"
        onSuccess={() => handleModalSuccess('Phone number verified!')}
      />

      {activeModal === 'identity-verification' && (
        <VerificationQuickAction
          isOpen
          onClose={handleModalClose}
          onSuccess={() => handleModalSuccess('Verification submitted!')}
        />
      )}

      <TwoFactorSetupModal
        isOpen={activeModal === 'two-factor-setup'}
        onClose={handleModalClose}
        onSuccess={() => handleModalSuccess('Two-factor authentication enabled!')}
      />
    </div>
  );
};

export default NotificationPanel;
